'use client';

import { Download } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';

interface PricingItem {
  translationKey: string;
  price: number;
}

interface PricingSummaryProps {
  selectedPlans: PricingItem[];
  selectedAddOns: PricingItem[];
  totalPrice: number;
  onExportPDF: () => void;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export function PricingSummary({ selectedPlans, selectedAddOns, totalPrice, onExportPDF }: PricingSummaryProps) {
  const t = useTranslations('pricing');
  const hasSelection = selectedPlans.length > 0 || selectedAddOns.length > 0;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg border-2 border-blue-200 dark:border-blue-800 p-6 sticky top-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t('calculator.summary.title')}
      </h3>

      <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto">
        {/* Selected Plans */}
        {selectedPlans.length > 0 ? (
          selectedPlans.map((plan) => {
            const getPlanName = () => {
              try {
                // Strip 'pricing.' prefix if present since we're already using 'pricing' namespace
                const cleanKey = plan.translationKey.startsWith('pricing.') 
                  ? plan.translationKey.replace(/^pricing\./, '') 
                  : plan.translationKey;
                return t(`${cleanKey}.name`) || plan.translationKey;
              } catch {
                return plan.translationKey;
              }
            };
            return (
              <div
                key={plan.translationKey}
                className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-white">{getPlanName()}</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(plan.price)}
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">{t('calculator.summary.noPlan')}</p>
        )}

        {/* Selected Add-ons */}
        {selectedAddOns.length > 0 ? (
          selectedAddOns.map((addon) => {
            const getAddonName = () => {
              try {
                // Strip 'pricing.' prefix if present since we're already using 'pricing' namespace
                const cleanKey = addon.translationKey.startsWith('pricing.') 
                  ? addon.translationKey.replace(/^pricing\./, '') 
                  : addon.translationKey;
                return t(`${cleanKey}.name`) || addon.translationKey;
              } catch {
                return addon.translationKey;
              }
            };
            return (
              <div
                key={addon.translationKey}
                className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-white">{getAddonName()}</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(addon.price)}
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">{t('calculator.summary.noAddons')}</p>
        )}
      </div>

      {/* Total Price */}
      <div className="border-t border-blue-200 dark:border-blue-700 pt-4 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-gray-900 dark:text-white">{t('calculator.summary.totalPrice')}</span>
          <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
            {formatCurrency(totalPrice)}
          </span>
        </div>
      </div>

      {/* Export Button */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (hasSelection) {

            onExportPDF();
          } else {

          }
        }}
        disabled={!hasSelection}
        className={cn(
          'w-full px-6 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2',
          hasSelection
            ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl cursor-pointer'
            : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
        )}
      >
        <Download className="h-5 w-5" />
        {t('calculator.summary.exportPdf')}
      </button>
    </div>
  );
}

